/**
 * Staff Portal v2 — reusable route wrapper.
 *
 * Wraps any staff-facing page with the v2 shell when the portal flag is ON;
 * renders children unchanged when OFF (legacy fallback). Centralises the
 * shell + flag lookup so App.js route registrations stay clean and every
 * integrated route follows the same rollback contract.
 *
 * Usage:
 *   <StaffPortalRoute title="My Roster" subtitle="Your upcoming shifts">
 *     <MyRoster />
 *   </StaffPortalRoute>
 *
 * Contract:
 *   • staff_portal_v2_enabled OFF → children rendered untouched
 *     (page's own layout continues to work — zero regression).
 *   • staff_portal_v2_enabled ON  → children rendered inside StaffPortalShell
 *     with the supplied title/subtitle.
 *   • While flags are loading, a minimal placeholder renders instead of
 *     flashing legacy chrome that would then swap to v2 chrome.
 *   • Route params and query strings pass through untouched — the wrapper
 *     never re-mounts children on flag resolution (children mount once
 *     inside whichever branch is selected).
 */
import React from 'react';
import { useLocation } from 'react-router-dom';
import StaffPortalShell from './StaffPortalShell';
import { useStaffV2Flags } from '../../lib/staffV2Flags';
import { useAuth } from '../../lib/auth';
import { isSharedAdminStaffPath } from '../layout/staffV2Paths';

export default function StaffPortalRoute({ title, subtitle, children }) {
  const flags = useStaffV2Flags();
  const location = useLocation();
  const { isAdmin } = useAuth();
  if (flags.loading) {
    return (
      <div
        className="min-h-[40vh] flex items-center justify-center text-sm text-slate-500"
        aria-busy="true"
        data-testid="staff-portal-route-loading"
      >
        Loading…
      </div>
    );
  }
  // Shared admin/staff routes (Incidents, Notifications, Staff Policies) render
  // inside the admin AppShell for elevated/management roles — the wrapper steps
  // aside so the page shows its normal chrome. Support Workers still get the
  // StaffPortalShell below. Staff-only routes are unaffected.
  if (isAdmin && isSharedAdminStaffPath(location.pathname)) {
    return children;
  }
  if (flags.staff_portal_v2_enabled) {
    // Inject `insideStaffPortal` so legacy pages can hide their own
    // duplicate H1 / breadcrumb chrome. Only patched onto direct React
    // element children; other node kinds are passed through untouched so
    // this stays a safe no-op for text, arrays, portals, etc.
    const patched = React.isValidElement(children)
      ? React.cloneElement(children, { insideStaffPortal: true })
      : children;
    return (
      <StaffPortalShell title={title} subtitle={subtitle}>
        {patched}
      </StaffPortalShell>
    );
  }
  return children;
}
